// Lumière : obscurité d'une carte (réglée carte par carte, et la nuit venue pour celles qui suivent le cycle
// jour/nuit) et sources de lumière qui la percent (éléments qui éclairent, équipement porté par un joueur).
// Partagé par le serveur (validation), le jeu (rendu) et l'administration.

/** Une source de lumière : elle éclaire un disque autour d'elle, plus fort au centre. */
export interface LightSource {
  /** Portée, en cases. */
  radius: number;
  /** Part de l'obscurité retirée au centre (0 : rien, 1 : comme en plein jour). */
  intensity: number;
  /** Teinte de la lueur. */
  color: string;
  /** Vitesse du vacillement, comme une flamme (0 : lumière fixe, 1 : torche, 3 : feu vif). */
  flicker: number;
}

export const LIGHT_LIMITS = { minRadius: 0.5, maxRadius: 12, maxFlicker: 3 } as const;
export const DEFAULT_LIGHT: LightSource = { radius: 3, intensity: 0.9, color: '#ffd28a', flicker: 1 };
/** Lueur de tout joueur sans autre lumière : on le voit toujours, même dans le noir complet. */
export const PLAYER_GLOW: LightSource = { radius: 0.7, intensity: 0.03, color: '#fff4e0', flicker: 0 };

/** Cycle jour/nuit, réglé dans l'onglet Configuration ; il ne touche que les cartes qui le suivent. */
export interface DayNightConfig {
  enabled: boolean;
  /** Durée d'une journée complète, en minutes réelles. */
  cycleMinutes: number;
  /** Obscurité au cœur de la nuit (0 à 1). */
  nightDarkness: number;
}

export const DAY_NIGHT_LIMITS = { minCycleMinutes: 2, maxCycleMinutes: 24 * 60 } as const;

/** Ce qui règle l'obscurité d'une carte. */
export interface MapLighting {
  /** Obscurité permanente (0 : aucune, 1 : noir complet sans lumière) : grotte, cave… */
  darkness: number;
  /** La nuit l'assombrit (carte en extérieur). */
  dayNight: boolean;
}

const smoothstep = (edge0: number, edge1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/**
 * Heure du jour, de 0 (minuit) à 1 (minuit suivant), en passant par 0,5 (midi). Calée sur l'horloge : la même
 * pour tous les joueurs.
 */
export const timeOfDay = (config: DayNightConfig, now: number): number => {
  const cycle = config.cycleMinutes * 60_000;
  return (now % cycle) / cycle;
};

/** Heure du jour (0 à 1) en clair : « 14 h 05 ». */
export const formatTimeOfDay = (time: number): string => {
  const minutes = Math.floor(time * 24 * 60);
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
};

/** Part de nuit (0 : plein jour, 1 : cœur de la nuit), avec des journées et des nuits longues et des transitions douces. */
export const nightFactor = (time: number): number => smoothstep(-0.25, 0.35, Math.cos(time * 2 * Math.PI));

/** Fait-il nuit dans le jeu (pour les {monstres} de nuit) ? Jamais si le cycle jour/nuit est désactivé. */
export const isNight = (config: DayNightConfig, now: number): boolean => config.enabled && nightFactor(timeOfDay(config, now)) >= 0.5;

/** Lever et coucher du soleil (0 hors transition, 1 au milieu) : teinte orangée. */
export const duskFactor = (night: number): number => 1 - Math.abs(2 * night - 1);

/** Lumière d'une carte à un instant donné. */
export interface Ambient {
  darkness: number;
  /** Force de la teinte du lever ou du coucher du soleil (voir duskFactor). */
  dusk: number;
  /** Part de nuit (voir nightFactor) : la teinte passe de l'or au violet à mesure qu'elle grandit. */
  night: number;
  /** Matin (lever du soleil) plutôt que soir (coucher). */
  rising: boolean;
}

/** Obscurité d'une carte à cet instant : la plus forte de son obscurité permanente et de celle de la nuit. */
export const darknessOf = (map: MapLighting, config: DayNightConfig, now: number): Ambient => darknessAt(map, config, timeOfDay(config, now));

/** Obscurité d'une carte à cette heure du jour (0 : minuit, 0,5 : midi). */
export function darknessAt(map: MapLighting, config: DayNightConfig, time: number): Ambient {
  if (!map.dayNight || !config.enabled) return { darkness: map.darkness, dusk: 0, night: 0, rising: false };
  const night = nightFactor(time);
  return { darkness: Math.max(map.darkness, config.nightDarkness * night), dusk: duskFactor(night), night, rising: time < 0.5 };
}

/** La plus forte des lumières (la plus grande portée), ou null. */
export function strongestLight(lights: Iterable<LightSource | null | undefined>): LightSource | null {
  let best: LightSource | null = null;
  for (const light of lights) if (light && (!best || light.radius > best.radius)) best = light;
  return best;
}
